import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import AsyncTable, { Column, RequestPage } from '../../../components/AsyncTable';
import Btn from '../../../components/Buttons/Btn';
import TableLoader from '../../../components/Loaders/TableLoader';
import { backendErrorNotification, frontendNotification } from '../../../components/Notification';
import { NotificationTypes } from '../../../components/Notification/notificationEnums';
import { ActionButtonsContainer } from '../../../components/TablePaginator/styles';
import HttpStatus from '../../../enums/httpStatus';
import { UrlPaths } from '../../../enums/urlPaths.enum';
import { DeleteIcon, EditIcon } from '../../../icons';
import history from '../../../services/history';
import { superAiAgentsApi, tenantsReq } from '../../../services/requests';
import { StoreState } from '../../../store/createStore';
import ContentContainer from '../../../templates/Content/ContentContainer';
import type { AiAgent } from '../../../types';
import CreateOrEditAiAgentModal from './components/CreateOrEditAiAgentModal';
import DeleteAiAgentModal from './components/DeleteAiAgentModal';
import { TenantOption } from './types';

const AiAgents = () => {
  const { user } = useSelector((state: StoreState) => state.auth);
  const { t } = useTranslation();
  const theme = useTheme();

  const [updateTable, setUpdateTable] = useState(0);
  const [tableData, setTableData] = useState<AiAgent[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateOrEditModal, setShowCreateOrEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAiAgent, setSelectedAiAgent] = useState<AiAgent | null>(null);

  const getDataForAsyncTable: RequestPage = async ({ search, page, quantityPerPage }) => {
    let hasMore = true;
    const source = superAiAgentsApi.axios.CancelToken.source();

    try {
      superAiAgentsApi.query = `?search=${search}&page=${page}&quantityPerPage=${quantityPerPage}`;
      const response = await superAiAgentsApi.index(source.token);

      if (response.status !== HttpStatus.OK) {
        throw response;
      }

      const { data }: { data: AiAgent[]; } = response.data;

      if (data.length === 0) {
        hasMore = false;
      }

      return {
        data,
        hasMore,
        totalPage: 0,
      };
    } catch (err) {
      if (!superAiAgentsApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }
  };

  const loadTenants = async () => {
    setIsLoading(true);

    try {
      const response = await tenantsReq.get({
        page: 1,
        quantityPerPage: 10,
      });

      if (response.status !== HttpStatus.OK) {
        throw response;
      }

      setTenants(response.data.data ?? []);
    } catch (err) {
      backendErrorNotification(err as AxiosError<any, any>);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (!user?.is_superadmin) {
      frontendNotification({
        message: 'Área restrita!',
        type: NotificationTypes.WARNING,
      });

      history.push(UrlPaths.LOGIN);
    }
  }, [user]);

  useEffect(() => {
    loadTenants();
  }, []);

  const handleCloseModals = () => {
    setShowCreateOrEditModal(false);
    setShowDeleteModal(false);
    setSelectedAiAgent(null);
  };

  const actionsButtons = (row: AiAgent) => (
    <ActionButtonsContainer type={5}>
      <EditIcon
        title={t('edit')}
        onClick={() => {
          setSelectedAiAgent(row);
          setShowCreateOrEditModal(true);
        }}
      />
      <DeleteIcon
        title={t('delete')}
        onClick={() => {
          setSelectedAiAgent(row);
          setShowDeleteModal(true);
        }}
      />
    </ActionButtonsContainer>
  );

  const columns: Column<AiAgent>[] = [
    {
      label: t('tenantName') as string,
      Cell: (row: AiAgent) => row.tenant ? row.tenant.name : row.tenant_id as unknown as string,
    },
    {
      label: t('aiAgent') as string,
      Cell: () => t('openAiChatGpt'),
    },
    {
      label: t('active') as string,
      Cell: (row: AiAgent) => (row.active ? t('aiAgentEnabled') : t('aiAgentDisabled')),
    },
    {
      label: t('gptUrl') as string,
      Cell: (row: AiAgent) => (
        <a href={row.data.url} target="_blank" rel="noreferrer">
          {row.data.url}
        </a>
      ),
    },
    {
      label: t('actions') as string,
      Cell: actionsButtons,
    },
  ];

  return (
    <ContentContainer title={t('aiAgents')}>
      <Btn
        text={t('registerAiAgent')}
        onClick={() => {
          setSelectedAiAgent(null);
          setShowCreateOrEditModal(true);
        }}
        style={{ marginBottom: '1rem' }}
      />

      {showCreateOrEditModal && (
        <CreateOrEditAiAgentModal
          showModal={showCreateOrEditModal}
          closeModal={handleCloseModals}
          callback={() => setUpdateTable((value) => value + 1)}
          tenants={tenants}
          selectedAiAgent={selectedAiAgent}
        />
      )}

      {showDeleteModal && selectedAiAgent && (
        <DeleteAiAgentModal
          showModal={showDeleteModal}
          closeModal={handleCloseModals}
          callback={() => setUpdateTable((value) => value + 1)}
          selectedAiAgent={selectedAiAgent}
        />
      )}

      <TableLoader isLoading={isLoading}>
        <AsyncTable
          tableName={t('aiAgents')}
          columns={columns}
          value={tableData}
          onChange={setTableData}
          requestPage={getDataForAsyncTable}
          reqListener={[updateTable]}
          options={{
            styles: {
              primaryColor: `${theme.colors.surface}`,
              secondaryColor: `${theme.colors.onSurface}`,
              alternateRowColor: theme.colors.onSurfaceLight,
              textColor: theme.colors.text,
            },
            quantityPerPageLabel: t('quantityPerPage'),
            searchPlaceholder: t('search'),
          }}
        />
      </TableLoader>
    </ContentContainer>
  );
};

export default AiAgents;